import {Component, Vue} from 'vue-property-decorator';
import moment, {Moment} from 'moment';
import {LocalizedString} from './types';
import ErrorHandler from './errors';
import Diff = moment.unitOfTime.Diff;

@Component
export class DateFormatter extends Vue {
    public static readonly DATE_PICKER_FORMAT = 'd.m.Y';
    private static readonly SERVER_DATE_FORMAT = 'YYYY/MM/DD';
    private static readonly CLIENT_DATE_FORMAT = 'DD.MM.YYYY';

    toClientDate(date: string): string {
        return date !== null ? moment(date, DateFormatter.SERVER_DATE_FORMAT).format(DateFormatter.CLIENT_DATE_FORMAT) : '';
    }

    getServerDate(date: string): Date {
        return date ? moment(date, DateFormatter.SERVER_DATE_FORMAT).toDate() : null;
    }

    getServerMoment(date: string) {
        return moment(date, DateFormatter.SERVER_DATE_FORMAT);
    }

    getClientMoment(date: string) {
        return moment(date, DateFormatter.CLIENT_DATE_FORMAT);
    }

    formatForServer(mom: Moment) {
        return mom.format(DateFormatter.SERVER_DATE_FORMAT);
    }
}

@Component
export class DateTimeFormatter extends Vue {
    public static readonly DATE_TIME_PICKER_FORMAT = 'd.m.Y H:i';
    private static readonly SERVER_DATE_FORMAT = 'YYYY/MM/DD HH:mm';
    private static readonly CLIENT_DATE_FORMAT = 'DD.MM.YYYY, HH:mm';

    toClientDateTime(date: string): string {
        return date !== null ? moment(date, DateTimeFormatter.SERVER_DATE_FORMAT).format(DateTimeFormatter.CLIENT_DATE_FORMAT) : '';
    }

    getServerDateTime(date: string): Date {
        return date ? moment(date, DateTimeFormatter.SERVER_DATE_FORMAT).toDate() : null;
    }

    getServerMomentDateTime(date: string) {
        return moment(date, DateTimeFormatter.SERVER_DATE_FORMAT);
    }

    getClientMomentDateTime(date: string) {
        return moment(date, DateTimeFormatter.CLIENT_DATE_FORMAT);
    }

    formatForServerDateTime(mom: Moment) {
        return mom.format(DateTimeFormatter.SERVER_DATE_FORMAT);
    }
}

@Component
export class DateUtils extends Vue {
    areDifferentDates(thisDate: Moment, thatDate: Moment): boolean {
        return !this.areSameDates(thisDate, thatDate);
    }

    areSameDates(thisDate: Moment, thatDate: Moment): boolean {
        return thisDate.diff(thatDate, 'days') === 0;
    }
}

@Component
export class DateTimeUtils extends Vue {
    areDifferentDateTimes(thisDate: Moment, thatDate: Moment, unitOfTime: Diff): boolean {
        return !this.areSameDateTimes(thisDate, thatDate, unitOfTime);
    }

    areSameDateTimes(thisDate: Moment, thatDate: Moment, unitOfTime: Diff): boolean {
        return thisDate.diff(thatDate, unitOfTime) === 0;
    }
}

@Component
export class SingularPluralUtil extends Vue {
    singularOrPluralLabel(baseLabel: string, elements: any) {
        let check = true;
        if (typeof elements === 'number') {
            check = elements === 1;
        } else {
            check = elements.length === 1;
        }
        return baseLabel + '.' + (check ? 'singular' : 'plural');
    }
}

@Component
export class StringFormatter extends Vue {
    stripLeadingZeros(numberValue: string) {
        return numberValue.replace(/^0+/, '');
    }

    joinUnique(elements: string[], delimiter: string, defaultValue: string): string {
        return elements ? elements.filter((value, index, self) => self.indexOf(value) === index).join(delimiter) : defaultValue;
    }

    formatMoneyValue(value: number, currency: string, defaultValue: string): string {
        return this.formatValue(value, currency, defaultValue);
    }

    formatAmountValue(value: number, unit: string, defaultValue: string): string {
        return this.formatValue(value, unit, defaultValue);
    }

    formatNumberGermanFallback(value: number) {
        return String(value.toFixed(2)).replace('.', ',');
    }

    valueOrDefault(value: string, defaultValue: string): string {
        return value || defaultValue;
    }

    translationForEnum(prefix: string, value: any) {
        return value ? this.$tc(prefix + '.' + value) : null;
    }

    private formatValue(value: number, textAfterValue: string, defaultValue: string): string {
        if (value === null) {
            return defaultValue;
        }

        const formattedValue = this.$n(value, 'currency') || this.formatNumberGermanFallback(value);

        if (textAfterValue) {
            return formattedValue + ' ' + textAfterValue;
        } else {
            return formattedValue;
        }
    }
}

@Component
export class LocalizedStringFormatter extends Vue {
    getTextForLanguage(localizedString: LocalizedString, language: string) {
        if (localizedString.supportedLanguages.indexOf(language) > -1) {
            return localizedString.values[language];
        } else {
            return null;
        }
    }

    getTextOfFirstLanguage(localizedString: LocalizedString) {
        if (!localizedString.supportedLanguages.empty()) {
            const firstLanguage: string = localizedString.supportedLanguages[0];
            return localizedString.values[firstLanguage];
        } else {
            return null;
        }
    }
}

@Component
export class TitleMixin extends Vue {
    static getTitle(vm) {
        const title = vm.title;
        if (title) {
            return typeof title === 'function' ? title.call(vm) : title;
        }
        return '';
    }

    mounted() {
        const title = TitleMixin.getTitle(this);
        if (title) {
            document.title = title;
        }
    }
}

@Component
export class LoggingUtils extends Vue {
    initLogging() {
        window.addEventListener('unhandledrejection', (event) => {
            ErrorHandler.handleError(event.reason)
                .then((success) => {
                    if (success) {
                        event.preventDefault();
                    }
                });
        });

        window.addEventListener('error', (event) => {
            ErrorHandler.handleError(event.error)
                .then((success) => {
                    if (success) {
                        event.preventDefault();
                    }
                });
        });
    }
}
